import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import { supabase } from "../supabase";
import { formatHoursMinutes } from "../helpers/timeHelpers";
import { formatSessionShiftLabel } from "../helpers/dateTimeHelpers";
import {
  DEFAULT_PAYROLL_REPORT_OPTIONS,
  formatPeriodLabel,
  resolvePayrollReportRows,
} from "../helpers/payrollReport";
import { PayrollReportOptionsScreen } from "./payroll/report/PayrollReportOptionsScreen";
import { PayrollReportPreviewScreen } from "./payroll/report/PayrollReportPreviewScreen";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY, DATA_SCOPE, VISIBILITY } from "../helpers/permissions";
import {
  applyOperationalTimeLogOrgScope,
  applyUserIdsToQuery,
  loadScopedUsers,
  PAYROLL_SCOPE_SELECTION,
  resolveOperationalOrganizationId,
  resolvePayrollSelectionUserIdsForQuery,
  selectOperationalTimeLogs,
} from "../helpers/scopedQueries";
import { blockDemoSave, shouldSkipSupabase } from "../helpers/guestMode";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { useOrganization } from "../context/OrganizationContext";
import {
  computeEmployeePayrollTotals,
  computeRowBaseGrossPay,
  getEmployeePayrollKey,
  resolveWeeklyOvertimeSettings,
} from "../helpers/payrollOvertime";
import {
  TIME_LOG_DATE_RANGE_OPTIONS,
  resolveTimeLogDateRangeBounds,
} from "../helpers/timeLogVisibility";
import { CPDropdown } from "./CPDropdown";
import { CPInput } from "./CPInput";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { PayrollQueueDesktop } from "./payroll/PayrollDesktop";
import { RecBar } from "./materials/MaterialRecordParts";
import "./project/projectForm.css";
import "./payroll/payrollQueueMobile.css";

function buildPayrollReportMeta(
  rows,
  startDate,
  endDate,
  { currentUser, employees = [], employeeScope = null } = {},
) {
  const uniqueEmployees = [
    ...new Set((rows || []).map((row) => row.employee).filter(Boolean)),
  ];
  const employeeName =
    uniqueEmployees.length === 1
      ? uniqueEmployees[0]
      : uniqueEmployees.length > 1
        ? `${uniqueEmployees.length} Employees`
        : rows[0]?.employee || "Unknown Employee";

  return {
    startDate,
    endDate,
    employeeName,
    employeeScope,
    employees,
    currentUser,
  };
}

export function PayrollQueueScreen({
  onBack,
  currentUser,
}) {
  const { can, scope, visibility, directReportIds, CAPABILITY } =
    usePermissions();
  const isDesktop = useIsDesktopViewport();
  const { settings } = useOrganization();
  const payrollScope = scope(CAPABILITY.PAYROLL_QUEUE);
  const showScopeSelector =
    payrollScope === DATA_SCOPE.TEAM ||
    payrollScope === DATA_SCOPE.ORGANIZATION;
  const canMarkPaid =
    can(CAPABILITY.PAYROLL_MARK_PAID) &&
    visibility(CAPABILITY.PAYROLL_MARK_PAID) === VISIBILITY.EDIT;

  const [dateRangeFilter, setDateRangeFilter] =
    useState("this_month");

  const [startDate, setStartDate] = useState(() => {
    return resolveTimeLogDateRangeBounds("this_month").startDate || "";
  });

  const [endDate, setEndDate] = useState(() => {
    return resolveTimeLogDateRangeBounds("this_month").endDate || "";
  });

  const [statusFilter, setStatusFilter] =
    useState("unpaid");

  const [selectedEmployee, setSelectedEmployee] =
    useState("");

  const [employees, setEmployees] =
    useState([]);

  const [rows, setRows] = useState([]);
  const [expandedEmployees, setExpandedEmployees] =
  useState({});

  const [selectedRows, setSelectedRows] =
    useState([]);

  const [isLoading, setIsLoading] =
    useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [isSaving, setIsSaving] =
    useState(false);

    const [submitted, setSubmitted] =
  useState(false);

  const [view, setView] = useState("queue");
  const [reportOptions, setReportOptions] = useState(
    DEFAULT_PAYROLL_REPORT_OPTIONS,
  );

  async function loadEmployees() {
    if (shouldSkipSupabase(currentUser)) {
      setEmployees([]);
      return;
    }

    if (!currentUser || !showScopeSelector) {
      setEmployees([]);
      return;
    }

    try {
      const data = await loadScopedUsers(
        supabase,
        currentUser,
        CAPABILITY.PAYROLL_QUEUE,
        "supabase_user_id, employee_name, hourly_rate, email, phone",
        { directReportIds },
      );
      setEmployees(data);
    } catch (error) {
      console.error("PAYROLL EMPLOYEE LOAD ERROR:", error);
      setEmployees([]);
    }
  }

  useEffect(() => {
    loadEmployees();

    if (payrollScope === DATA_SCOPE.TEAM) {
      setSelectedEmployee(PAYROLL_SCOPE_SELECTION.MY_CREW);
    } else if (payrollScope === DATA_SCOPE.ORGANIZATION) {
      setSelectedEmployee(PAYROLL_SCOPE_SELECTION.ALL_EMPLOYEES);
    } else {
      setSelectedEmployee(PAYROLL_SCOPE_SELECTION.MY_TIME);
    }
  }, [currentUser, payrollScope, directReportIds]);

  useEffect(() => {
    if (dateRangeFilter === "custom_range") {
      return;
    }

    const bounds = resolveTimeLogDateRangeBounds(dateRangeFilter, {
      orgSettings: settings,
    });

    if (bounds.startDate) {
      setStartDate(bounds.startDate);
    }

    if (bounds.endDate) {
      setEndDate(bounds.endDate);
    }
  }, [dateRangeFilter, settings]);

  async function handleLoadQueue() {
    if (shouldSkipSupabase(currentUser)) {
      setRows([]);
      setIsLoading(false);
      return;
    }

    if (startDate && endDate && startDate > endDate) {
      return;
    }

    try {
      setIsLoading(true);
      setSelectedRows([]);

      let query = selectOperationalTimeLogs(
        supabase,
        `
    id,
    employee,
    project_name,
    date_iso,
    start_time,
    end_time,
    total_hours,
    break_seconds,
    payroll_status,
    supabase_user_id,
    classification_name,
    payroll_type,
    trade_name,
    hourly_rate
  `,
      )
        .gte("date_iso", startDate)
        .lte("date_iso", endDate)
        .order("date_iso", {
          ascending: false,
        });

      const payrollUserIds = await resolvePayrollSelectionUserIdsForQuery(
        supabase,
        currentUser,
        selectedEmployee,
        { directReportIds },
      );
      const organizationId = await resolveOperationalOrganizationId(
        currentUser,
        supabase,
        { directReportIds },
      );

      query = applyUserIdsToQuery(query, payrollUserIds);
      query = applyOperationalTimeLogOrgScope(query, organizationId);

      if (statusFilter !== "all") {
        query = query.eq(
          "payroll_status",
          statusFilter
        );
      }

      const { data, error } = await query;

      if (error) throw error;

      const mappedRows = (data || []).map((row) => ({
        ...row,
        payroll_status: row.payroll_status || "unpaid",
        original_payroll_status: row.payroll_status || "unpaid",
        gross_pay: computeRowBaseGrossPay(row),
      }));

      setRows(mappedRows);
      setHasLoaded(true);
    } catch (err) {
      console.error(
        "PAYROLL QUEUE LOAD ERROR:",
        err
      );

      alert("Unable to load payroll queue.");
    } finally {
      setIsLoading(false);
    }
  }

  function toggleRowStatus(logId) {
    setRows((prev) =>
      prev.map((row) =>
        row.id === logId
          ? {
              ...row,
              payroll_status:
                row.payroll_status === "paid"
                  ? "unpaid"
                  : "paid",
            }
          : row
      )
    );
  }

  function toggleSelected(logId) {
    setSelectedRows((prev) =>
      prev.includes(logId)
        ? prev.filter((id) => id !== logId)
        : [...prev, logId]
    );
  }

  function toggleSelectAll() {
    if (selectedRows.length === rows.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(
        rows.map((row) => row.id)
      );
    }
  }

  function markSelected(status) {
    setRows((prev) =>
      prev.map((row) =>
        selectedRows.includes(row.id)
          ? {
              ...row,
              payroll_status: status,
            }
          : row
      )
    );
  }

  async function handleSaveChanges() {
    if (blockDemoSave(currentUser)) {
      return;
    }

    try {
      setIsSaving(true);

      const changedRows = rows.filter(
        (row) =>
          row.payroll_status !==
          row.original_payroll_status
      );

      for (const row of changedRows) {
        const { error } = await supabase
          .from("time_logs")
          .update({
  payroll_status:
    row.payroll_status,

  paid_date:
    row.payroll_status === "paid"
      ? new Date().toISOString()
      : null,
})
          .eq("id", row.id);

        if (error) throw error;
      }

      setSubmitted(true);

      setRows((prev) =>
        prev.map((row) => ({
          ...row,
          original_payroll_status:
            row.payroll_status,
        }))
      );

      setSelectedRows([]);
    } catch (err) {
      console.error(
        "PAYROLL QUEUE SAVE ERROR:",
        err
      );

      alert("Unable to save payroll changes.");
    } finally {
      setIsSaving(false);
    }
  }
  const groupedRows = rows.reduce(
  (acc, row) => {
    const employeeKey = getEmployeePayrollKey(row);

    if (!acc[employeeKey]) {
      acc[employeeKey] = {
        employeeName:
          row.employee ||
          "Unknown Employee",
        rows: [],
      };
    }

    acc[employeeKey].rows.push(row);

    return acc;
  },
  {}
);

  const weeklyOvertimeSettings = resolveWeeklyOvertimeSettings(settings);

  const employeePayrollTotals = Object.fromEntries(
    Object.entries(groupedRows).map(([employeeKey, employeeData]) => [
      employeeKey,
      computeEmployeePayrollTotals(employeeData.rows, weeklyOvertimeSettings),
    ]),
  );

  // Selection overrides report inclusion only; Mark Paid still uses selectedRows separately.
  const reportRows = resolvePayrollReportRows(rows, selectedRows);

  const reportMeta = buildPayrollReportMeta(reportRows, startDate, endDate, {
    currentUser,
    employees,
    employeeScope: selectedEmployee || PAYROLL_SCOPE_SELECTION.MY_TIME,
    orgSettings: settings,
  });

  const handleHardwareBack = useCallback(() => {
    if (submitted) {
      setSubmitted(false);
      return true;
    }

    if (view === "report-preview") {
      setView("report-options");
      return true;
    }

    if (view === "report-options") {
      setView("queue");
      return true;
    }

    onBack?.();
    return true;
  }, [onBack, submitted, view]);

  useHardwareBackHandler(handleHardwareBack);

  if (view === "report-options") {
    return (
      <PayrollReportOptionsScreen
        reportTitle={reportMeta.employeeName}
        options={reportOptions}
        onChange={setReportOptions}
        onPreview={(mergedOptions) => {
          setReportOptions(mergedOptions);
          setView("report-preview");
        }}
        onBack={() => setView("queue")}
      />
    );
  }

  if (view === "report-preview") {
    return (
      <PayrollReportPreviewScreen
        rows={reportRows}
        meta={reportMeta}
        options={reportOptions}
        onBack={() => setView("report-options")}
        onEditOptions={() => setView("report-options")}
      />
    );
  }

if (submitted) {
  return (
    <div className="screen nx-screen ci-success-screen">
      <div className="ci-success-content">
        <div className="ci-success-icon">
          <svg
            viewBox="0 0 52 52"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              className="checkmark-circle nx-checkmark-circle"
              cx="26"
              cy="26"
              r="24"
            />
            <path
              className="checkmark-check nx-checkmark-check"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
            />
          </svg>
        </div>

        <h1 className="ci-success-title">
          Payroll Updated
        </h1>

        <p className="ci-success-sub">
          Payroll changes were saved successfully.
        </p>

        <button
          className="nx-btn nx-btn-gold"
          style={{ marginTop: "12px" }}
          onClick={() =>
            setSubmitted(false)
          }
        >
          Continue Processing
        </button>
      </div>
    </div>
  );
}
  if (isDesktop) {
    return (
      <PayrollQueueDesktop
        onBack={onBack}
        dateRangeFilter={dateRangeFilter}
        onDateRange={setDateRangeFilter}
        startDate={startDate}
        endDate={endDate}
        onStartDate={setStartDate}
        onEndDate={setEndDate}
        statusFilter={statusFilter}
        onStatus={setStatusFilter}
        showScopeSelector={showScopeSelector}
        selectedEmployee={selectedEmployee}
        onEmployee={setSelectedEmployee}
        payrollScope={payrollScope}
        scopeValues={{ team: DATA_SCOPE.TEAM, organization: DATA_SCOPE.ORGANIZATION }}
        employees={employees}
        isLoading={isLoading}
        onLoad={handleLoadQueue}
        rows={rows}
        groupedRows={groupedRows}
        employeePayrollTotals={employeePayrollTotals}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        canMarkPaid={canMarkPaid}
        onToggleStatus={toggleRowStatus}
        onMarkSelected={markSelected}
        onExport={() => {
          setReportOptions(DEFAULT_PAYROLL_REPORT_OPTIONS);
          setView("report-options");
        }}
        onSave={handleSaveChanges}
        isSaving={isSaving}
        hasLoaded={hasLoaded}
      />
    );
  }

  const statusField = (
    <CPDropdown
      label="Status"
      value={statusFilter}
      onChange={setStatusFilter}
      includePlaceholder={false}
      options={[
        { value: "unpaid", label: "Unpaid" },
        { value: "paid", label: "Paid" },
        { value: "all", label: "All" },
      ]}
    />
  );

  const scopeField = showScopeSelector ? (
    <CPDropdown
      label="Scope"
      value={selectedEmployee}
      onChange={setSelectedEmployee}
      includePlaceholder={false}
      options={[
        ...(payrollScope === DATA_SCOPE.TEAM
          ? [
              { value: "MY_CREW", label: "My Crew" },
              { value: "", label: "My Time" },
            ]
          : []),
        ...(payrollScope === DATA_SCOPE.ORGANIZATION
          ? [
              { value: "ALL_EMPLOYEES", label: "All Employees" },
              { value: "", label: "My Time" },
            ]
          : []),
        ...employees.map((employee) => ({
          value: employee.supabase_user_id,
          label: employee.employee_name,
        })),
      ]}
    />
  ) : null;

  return (
    <div
      className="screen nx-screen rec-page pf-page pq-page"
      data-testid="payroll-workspace"
    >
      <RecBar title="Payroll" leading="back" onBack={onBack} />
      <div className="pf-subtitle">Payroll processing</div>
      <section className="rec-card pq-controls" aria-label="Payroll filters">
        <CPDropdown
          label="Date Range"
          value={dateRangeFilter}
          onChange={setDateRangeFilter}
          includePlaceholder={false}
          options={TIME_LOG_DATE_RANGE_OPTIONS}
        />

        {dateRangeFilter === "custom_range" && (
          <>
            <p className="pq-help">
              Custom Range uses the Start and End dates below as the loaded
              payroll period.
            </p>
            <div className="cp-row-grid-2">
              <CPInput
                label="Start Date"
                type="date"
                value={startDate}
                onChange={setStartDate}
              />
              <CPInput
                label="End Date"
                type="date"
                value={endDate}
                onChange={setEndDate}
              />
            </div>
            {startDate && endDate && startDate > endDate ? (
              <p className="pq-alert" role="alert">
                Start must be on or before End.
              </p>
            ) : null}
          </>
        )}

        {(startDate || endDate) && (
          <p className="pq-period" aria-label="Payroll period">
            {formatPeriodLabel(startDate, endDate)}
          </p>
        )}

        {scopeField ? (
          <div className="cp-row-grid-2" data-testid="payroll-status-scope">
            {statusField}
            {scopeField}
          </div>
        ) : (
          statusField
        )}

        <button
          type="button"
          className="rec-btn rec-btn--primary pq-load"
          onClick={handleLoadQueue}
          disabled={
            !startDate ||
            !endDate ||
            startDate > endDate ||
            isLoading
          }
        >
          {isLoading ? "Loading..." : "Load Payroll Queue"}
        </button>
      </section>

      {rows.length > 0 && (
        <div
          className="rb-card"
          style={{ marginTop: "20px" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                color: "var(--nx-gold)",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Queue Entries
            </div>

            <button
              onClick={toggleSelectAll}
              style={{
                background: "transparent",
                border:
                  "1px solid var(--nx-gold)",
                color: "var(--nx-gold)",
                borderRadius: "999px",
                padding: "6px 10px",
                fontSize: "12px",
              }}
            >
              {selectedRows.length ===
              rows.length
                ? "Clear All"
                : "Select All"}
            </button>
          </div>

          {selectedRows.length > 0 && canMarkPaid && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <button
                className="rb-generate-btn"
                onClick={() =>
                  markSelected("paid")
                }
              >
                Mark Paid
              </button>

              <button
                className="rb-generate-btn"
                onClick={() =>
                  markSelected("unpaid")
                }
              >
                Mark Unpaid
              </button>
            </div>
          )}

          {Object.entries(groupedRows).map(
  ([employeeId, employeeData]) => {

    const employeeName =
      employeeData.employeeName;

    const employeeRows =
      employeeData.rows;
    const payrollTotals =
      employeePayrollTotals[employeeId] || {
        totalHours: 0,
        regularHours: 0,
        overtimeHours: 0,
        grossPay: 0,
      };
    const totalHours = payrollTotals.totalHours;
    const grossPayroll = payrollTotals.grossPay;

    return (
    <div key={employeeName}>
  <div
    onClick={() =>
      setExpandedEmployees((prev) => ({
        ...prev,
        [employeeName]:
          !prev[employeeName],
      }))
    }
    style={{
      background:
        "rgba(255,255,255,0.03)",
      border:
        "1px solid rgba(255,215,0,0.25)",
      borderRadius: "12px",
      padding: "14px",
      marginTop: "20px",
      marginBottom: "12px",
      cursor: "pointer",
    }}
  >
  <div
    style={{
      color: "var(--nx-gold)",
      fontSize: "18px",
      fontWeight: 700,
    }}
  >
    {expandedEmployees[employeeName]
  ? "▲ "
  : "▼ "}
{employeeName}
  </div>

  <div
    style={{
      color: "#8db8ff",
      marginTop: "8px",
      fontSize: "13px",
    }}
  >
    Entries: {employeeRows.length}
  </div>

  <div
    style={{
      color: "var(--nx-text, #1a2233)",
      marginTop: "4px",
      fontSize: "13px",
    }}
  >
    Hours:{" "}
    {formatHoursMinutes(
      totalHours
    )}
  </div>

  {payrollTotals.overtimeHours > 0 && (
    <div
      style={{
        color: "var(--nx-text, #1a2233)",
        marginTop: "4px",
        fontSize: "13px",
      }}
    >
      Regular: {payrollTotals.regularHours.toFixed(1)} hrs · Overtime:{" "}
      {payrollTotals.overtimeHours.toFixed(1)} hrs
    </div>
  )}

  <div
    style={{
      color: "var(--nx-green, #178a4c)",
      marginTop: "4px",
      fontWeight: 700,
    }}
  >
    Gross Payroll: $
    {grossPayroll.toFixed(2)}
  </div>
</div>

{expandedEmployees[employeeName] &&
  employeeRows.map((row) => (
            <div
              key={row.id}
              style={{
                padding: "14px 0",
                borderBottom:
                  "1px solid var(--nx-border, #e2e5eb)",
                display: "flex",
                gap: "12px",
              }}
            >
              <input
                type="checkbox"
                checked={selectedRows.includes(
                  row.id
                )}
                onChange={() =>
                  toggleSelected(row.id)
                }
                style={{
                  marginTop: "4px",
                }}
              />

              <div style={{ flex: 1 }}>
                <>
  <div
  style={{
    color: "var(--nx-text, #1a2233)",
    fontWeight: 600,
  }}
>
    {new Date(
      row.date_iso +
        "T12:00:00"
    ).toLocaleDateString(
      "en-US",
      {
        month: "long",
        day: "numeric",
        year: "numeric",
      }
    )}
  </div>
</>

                <div
                  style={{
                    color: "var(--nx-gold)",
                    marginTop: "4px",
                    fontWeight: 600,
                  }}
                >
                  {row.project_name ||
                    "No Project"}
                </div>
                <div
  style={{
    color: "rgba(255,255,255,0.75)",
    fontSize: "13px",
    marginTop: "4px",
  }}
>
  {row.trade_name ? `${row.trade_name} • ` : ""}
  {row.classification_name || "No Classification"}
  {" • "}
  {row.payroll_type || "No Payroll Type"}
</div>

<div
  style={{
    color: "#4ade80",
    fontSize: "13px",
    marginTop: "2px",
    fontWeight: 600,
  }}
>
  ${Number(row.hourly_rate || 0).toFixed(2)}/hr
</div>

                <div
                  style={{
                    color: "#8db8ff",
                    fontSize: "13px",
                    marginTop: "4px",
                  }}
                >
                  {row.start_time && row.end_time
                    ? formatSessionShiftLabel(row.start_time, row.end_time)
                    : "N/A"}
                </div>

                <div
                  style={{
                    color: "var(--nx-gold)",
                    marginTop: "6px",
                  }}
                >
                  Paid:{" "}
                  {formatHoursMinutes(
                    row.total_hours
                  )}
                </div>

                <div
                  style={{
                    color: "#ef4444",
                    fontSize: "13px",
                    marginTop: "2px",
                    fontWeight: 600,
                  }}
                >
                  Breaks:{" "}
                  {Math.floor(
                    (row.break_seconds || 0) /
                      3600
                  )}
                  h{" "}
                  {Math.floor(
                    ((row.break_seconds || 0) %
                      3600) /
                      60
                  )}
                  m
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      color: "#4ade80",
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    ${row.gross_pay.toFixed(2)}
                  </div>

                  <button
                    onClick={() =>
                      toggleRowStatus(
                        row.id
                      )
                    }
                    style={{
                      padding: "4px 10px",
                      borderRadius: "999px",
                      fontSize: "11px",
                      fontWeight: 700,
                      backgroundColor:
                        row.payroll_status ===
                        "paid"
                          ? "rgba(34,197,94,0.18)"
                          : "rgba(239,68,68,0.18)",
                      color:
                        row.payroll_status ===
                        "paid"
                          ? "#22c55e"
                          : "#ef4444",
                      border:
                        row.payroll_status ===
                        "paid"
                          ? "1px solid #22c55e"
                          : "1px solid #ef4444",
                    }}
                  >
                    {row.payroll_status ===
                    "paid"
                      ? "PAID"
                      : "UNPAID"}
                  </button>
                </div>
              </div>
            </div>
                            ))
}
    </div>
    );
  }
)}

          <div
  style={{
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    flexWrap: "wrap",
  }}
>
  <button
    className="rb-generate-btn"
    onClick={() => {
      setReportOptions(DEFAULT_PAYROLL_REPORT_OPTIONS);
      setView("report-options");
    }}
  >
    Export Report
  </button>

  <button
    className="rb-generate-btn"
    onClick={handleSaveChanges}
    disabled={isSaving}
  >
    {isSaving
      ? "Saving..."
      : "Save Changes"}
  </button>
</div>
        </div>
      )}
    </div>
  );
}